import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MenuIcon, XIcon, ChevronDownIcon, SearchIcon, UserIcon } from 'lucide-react';
type Language = 'ua' | 'en';
type HeaderProps = {
  language: Language;
  onLanguageChange: (lang: Language) => void;
};
const navItems = {
  ua: ['Про нас', 'Продукція', 'Технічний центр', 'Цифровий центр', 'Контакти'],
  en: ['About', 'Products', 'Technical Center', 'Digital Center', 'Contact']
};
const ctaText = {
  ua: 'Стати партнером',
  en: 'Become a Partner'
};
export function Header({
  language,
  onLanguageChange
}: HeaderProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  return <motion.header initial={{
    y: -100
  }} animate={{
    y: 0
  }} transition={{
    duration: 0.6,
    ease: [0.22, 1, 0.36, 1]
  }} className="fixed top-0 left-0 right-0 z-50 flex flex-col" data-id="element-113">
      {/* Desktop Top Bar (Technical) */}
      <div className="hidden lg:block bg-[#1a1a1a] relative z-20" data-id="element-114">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-115">
          <div className="flex items-center justify-end h-10 gap-6" data-id="element-116">
            {/* Search Field */}
            <div className="relative flex items-center" data-id="element-117">
              <SearchIcon className="w-3.5 h-3.5 text-gray-400 absolute left-3" data-id="element-118" />
              <input type="text" placeholder={language === 'ua' ? 'Пошук...' : 'Search...'} className="bg-black/50 border border-white/10 rounded-full py-1 pl-9 pr-4 text-xs text-gray-300 focus:outline-none focus:border-chamaleon-yellow/50 focus:text-white w-48 transition-all" data-id="element-119" />
            </div>

            {/* Language Switcher */}
            <div className="relative" data-id="element-120">
              <button onClick={() => setIsLangMenuOpen(!isLangMenuOpen)} className="flex items-center gap-1 text-xs font-medium text-gray-400 hover:text-white transition-colors" data-id="element-121">
                {language.toUpperCase()}
                <ChevronDownIcon className={`w-3 h-3 transition-transform ${isLangMenuOpen ? 'rotate-180' : ''}`} data-id="element-122" />
              </button>

              <AnimatePresence data-id="element-123">
                {isLangMenuOpen && <motion.div initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: 10
              }} transition={{
                duration: 0.2
              }} className="absolute top-full right-0 mt-2 w-24 bg-chamaleon-charcoal border border-white/10 rounded-lg shadow-xl overflow-hidden z-50" data-id="element-124">
                    {(['en', 'ua'] as Language[]).map(lang => <button key={lang} onClick={() => {
                  onLanguageChange(lang);
                  setIsLangMenuOpen(false);
                }} className={`w-full text-left px-4 py-2 text-xs transition-colors ${language === lang ? 'bg-chamaleon-yellow text-chamaleon-black font-medium' : 'text-gray-300 hover:bg-white/5 hover:text-white'}`} data-id="element-125">
                        {lang.toUpperCase()}
                      </button>)}
                  </motion.div>}
              </AnimatePresence>
            </div>

            {/* Personal Cabinet */}
            <button className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-white transition-colors" data-id="element-126">
              <UserIcon className="w-3.5 h-3.5" data-id="element-127" />
              <span data-id="element-128">
                {language === 'ua' ? 'Особистий кабінет' : 'Personal Cabinet'}
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Yellow Accent Separator */}
      <div className="hidden lg:block h-[2px] w-full bg-gradient-to-r from-transparent via-chamaleon-yellow to-transparent opacity-80 relative z-20" data-id="element-129" />

      {/* Main Bar */}
      <div className={`transition-all duration-500 relative z-10 ${isScrolled ? 'bg-chamaleon-black/95 backdrop-blur-xl shadow-lg' : 'bg-chamaleon-black/40 backdrop-blur-sm lg:bg-transparent lg:backdrop-blur-none'}`} data-id="element-130">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-131">
          <div className="flex items-center justify-between h-16 lg:h-20" data-id="element-132">
            {/* Logo */}
            <motion.a href="#" className="flex items-center gap-2 group" whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} data-id="element-133">
              <div className="relative flex items-center h-8 sm:h-10" data-id="element-134">
                <img src="https://cdn.magicpatterns.com/uploads/dPC9v62jFFRaqiBcJdHaJe/chamaeleon_logo_white-yellow_RGB_копія.png" alt="CHAMÄLEON" className="h-full w-auto object-contain" data-id="element-135" />
                <div className="absolute inset-0 bg-chamaleon-yellow/20 rounded-lg blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-136" />
              </div>
            </motion.a>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-8" data-id="element-137">
              {navItems[language].map((item, index) => <motion.a key={item} href="#" className="text-sm font-medium text-gray-300 hover:text-white transition-colors relative group" initial={{
              opacity: 0,
              y: -10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: index * 0.05 + 0.3
            }} data-id="element-138">
                  {item}
                  <span className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-0 h-0.5 bg-chamaleon-yellow group-hover:w-full transition-all duration-300" data-id="element-139" />
                </motion.a>)}
            </nav>

            {/* Desktop CTA Button */}
            <div className="hidden lg:block" data-id="element-140">
              <motion.a href="#partner" className="inline-flex items-center justify-center px-5 py-2.5 bg-chamaleon-yellow text-chamaleon-black text-sm font-bold rounded-lg hover:bg-[#E5A619] transition-colors shadow-[0_0_15px_rgba(255,184,28,0.3)] hover:shadow-[0_0_25px_rgba(255,184,28,0.5)]" whileHover={{
              scale: 1.05
            }} whileTap={{
              scale: 0.95
            }} initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: 0.5
            }} data-id="element-141">
                {ctaText[language]}
              </motion.a>
            </div>

            {/* Mobile Right Section */}
            <div className="flex lg:hidden items-center gap-4" data-id="element-142">
              {/* Mobile Language Switcher */}
              <div className="relative" data-id="element-143">
                <button onClick={() => setIsLangMenuOpen(!isLangMenuOpen)} className="flex items-center gap-1 text-sm font-medium text-white" data-id="element-144">
                  {language.toUpperCase()}
                  <ChevronDownIcon className={`w-4 h-4 transition-transform ${isLangMenuOpen ? 'rotate-180' : ''}`} data-id="element-145" />
                </button>

                <AnimatePresence data-id="element-146">
                  {isLangMenuOpen && <motion.div initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: 10
                }} transition={{
                  duration: 0.2
                }} className="absolute top-full right-0 mt-2 w-24 bg-chamaleon-charcoal border border-white/10 rounded-lg shadow-xl overflow-hidden z-50" data-id="element-147">
                      {(['en', 'ua'] as Language[]).map(lang => <button key={lang} onClick={() => {
                    onLanguageChange(lang);
                    setIsLangMenuOpen(false);
                  }} className={`w-full text-left px-4 py-2 text-sm transition-colors ${language === lang ? 'bg-chamaleon-yellow text-chamaleon-black font-medium' : 'text-gray-300 hover:bg-white/5 hover:text-white'}`} data-id="element-148">
                          {lang.toUpperCase()}
                        </button>)}
                    </motion.div>}
                </AnimatePresence>
              </div>

              {/* Mobile Menu Button */}
              <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="p-2 -mr-2 text-white" aria-label="Toggle menu" data-id="element-149">
                {isMobileMenuOpen ? <XIcon size={24} data-id="element-150" /> : <MenuIcon size={24} data-id="element-151" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence data-id="element-152">
          {isMobileMenuOpen && <motion.div initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} exit={{
          opacity: 0,
          height: 0
        }} transition={{
          duration: 0.3
        }} className="lg:hidden bg-chamaleon-black/95 backdrop-blur-xl border-t border-white/5 overflow-hidden" data-id="element-153">
              <nav className="max-w-7xl mx-auto px-4 py-6 space-y-1" data-id="element-154">
                {/* Mobile Search and User */}
                <div className="flex flex-col gap-4 px-4 py-4 mb-4 border-b border-white/10 bg-white/5 rounded-xl" data-id="element-155">
                  <div className="relative flex items-center w-full" data-id="element-156">
                    <SearchIcon className="w-5 h-5 text-gray-400 absolute left-3" data-id="element-157" />
                    <input type="text" placeholder={language === 'ua' ? 'Пошук...' : 'Search...'} className="bg-black/50 border border-white/10 rounded-full py-2.5 pl-10 pr-4 text-sm text-white focus:outline-none focus:border-chamaleon-yellow/50 w-full transition-all" data-id="element-158" />
                  </div>
                  <button className="flex items-center gap-3 text-gray-300 hover:text-white transition-colors px-2" data-id="element-159">
                    <UserIcon className="w-5 h-5" data-id="element-160" />
                    <span className="font-medium" data-id="element-161">
                      {language === 'ua' ? 'Особистий кабінет' : 'Personal Cabinet'}
                    </span>
                  </button>
                </div>

                {/* Mobile Nav Links */}
                {navItems[language].map((item, index) => <motion.a key={item} href="#" className="block px-4 py-3 rounded-lg text-gray-300 hover:text-white hover:bg-white/5 transition-colors font-medium" initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: index * 0.05
            }} onClick={() => setIsMobileMenuOpen(false)} data-id="element-162">
                    {item}
                  </motion.a>)}

                {/* Mobile CTA */}
                <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.3
            }} className="pt-4 mt-2 border-t border-white/10" data-id="element-163">
                  <a href="#partner" className="flex items-center justify-center w-full px-4 py-3.5 bg-chamaleon-yellow text-chamaleon-black font-bold rounded-xl hover:bg-[#E5A619] transition-colors" onClick={() => setIsMobileMenuOpen(false)} data-id="element-164">
                    {ctaText[language]}
                  </a>
                </motion.div>
              </nav>
            </motion.div>}
        </AnimatePresence>
      </div>
    </motion.header>;
}